import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject } from '@angular/core';
import { INIT_MODES, IReportTabState, InitMode } from '@navstation-portal/reporting/data-model/vessel-report';
import {
  BreakdownConsumptionsFormState,
  ConsumptionsState,
  FuelSpecFormState,
  FuelSpecState,
  InertGasSystemState,
  InertGasSystemsFormState,
  TotalConsumptionsFormState,
} from '@navstation-portal/reporting/data-model/vessel-report';
import { SelectOption } from '@navstation-portal/shared/data-model/common';
import { VesselSpecStore } from '@navstation-portal/vessels/data-access/store';
import { signalStore, withComputed, withFeature, withMethods, withState } from '@ngrx/signals';
import { initStateField } from '../../utils/store.utility';
import { PowerStore } from '../power/power.store';
import { withCalculatedValues } from '../with-calculated-values';
import { withFormStoreReducer } from '../with-form-store-reducer';
import { collectConsumptionsCalculatedValues } from './consumptions-calculated-values';
import { consumptionsFormEvents } from './consumptions-events';
import { buildConsumptionsVm } from './consumptions-vm-builder';
import { withConsumptionsEffects } from './with-consumptions-effects';
import { withConsumptionsReducer } from './with-consumptions-reducer';

const initialConsumptionStateReducer = (initialData: IReportTabState, initMode: InitMode) => {
  const data = initialData as ConsumptionsState;

  switch (initMode) {
    case INIT_MODES.CARRY_OVER:
      return initWithCarryOver(data);
    case INIT_MODES.IMPORT:
      return initWithExistingReport(data);
    case INIT_MODES.BLANK:
      return initBlank();
    default:
      return initBlank();
  }
};

const initWithExistingReport = (data: ConsumptionsState) => {
  return {
    formState: {
      water: {
        fwgNonFunctional: initStateField<boolean>(data.fwgNonFunctional),
        freshWaterProduced: initStateField<number>(data.freshWaterProduced),
        distilledWaterProduced: initStateField<number>(data.distilledWaterProduced),
      },
      inertGasSystems: {
        entries: generateInertGasSystemsArray(data.inertGasSystems),
      },
      cylinderOilConsumption: {
        oilCode: initStateField<SelectOption>(data.cylinderOilConsumption.oilCode),
        oilBn: initStateField<number>(data.cylinderOilConsumption.oilBn),
        oilDensityAt15: initStateField<number>(data.cylinderOilConsumption.oilDensityAt15),
        oilTankTemperature: initStateField<number>(data.cylinderOilConsumption.oilTankTemperature),
        oilConsumption: initStateField<number>(data.cylinderOilConsumption.oilConsumption),
        oilKgConsumption: initStateField<number>(data.cylinderOilConsumption.oilKgConsumption),
        oilSloc: initStateField<number>(data.cylinderOilConsumption.oilSloc),
      },
      fuelSpecs: { entries: generateFuelSpecArray(data) },
      totalConsumptions: { entries: generateTotalConsumptionsArray(data) },
      auxEngineConsumptions: {
        entries: generateAuxEnginesConsumptionsArray(data),
      },
      boilerConsumptions: { entries: generateBoilerConsumptionsArray(data) },
    },
  };
};

const generateInertGasSystemsObject = (data: InertGasSystemState) => {
  const inertGasSystem: InertGasSystemsFormState = {
    from: initStateField<Date>(data.from),
    to: initStateField<Date>(data.to),
  };
  return inertGasSystem;
};

// V1 always draws four runs, and the adapter maps four.
const INERT_GAS_SYSTEM_RUNS = 4;

const generateInertGasSystemsArray = (data?: InertGasSystemState[]) => {
  if (!data) {
    return Array.from({ length: INERT_GAS_SYSTEM_RUNS }, () =>
      generateInertGasSystemsObject({ from: undefined, to: undefined })
    );
  }

  const result: InertGasSystemsFormState[] = data.map((item) => {
    return generateInertGasSystemsObject(item);
  });

  return result;
};

const generateFuelSpec = (fuelSpec?: FuelSpecState): FuelSpecFormState => {
  return {
    fuelType: initStateField<SelectOption>(fuelSpec?.fuelType),
    lcv: initStateField<number>(fuelSpec?.lcv),
    sulphur: initStateField<number>(fuelSpec?.sulphur),
    density: initStateField<number>(fuelSpec?.density),
    fuelTemp: initStateField<number>(fuelSpec?.fuelTemp),
    viscosity: initStateField<number>(fuelSpec?.viscosity),
  };
};

const generateFuelSpecArray = (data?: ConsumptionsState): FuelSpecFormState[] => {
  if (!data) return [generateFuelSpec(), generateFuelSpec(), generateFuelSpec()];

  return data.fuelSpecs?.map((fuelSpec) => generateFuelSpec(fuelSpec)) || [];
};

const generateTotalConsumption = (
  data?: ConsumptionsState,
  fuelSpec?: string | undefined
): TotalConsumptionsFormState => {
  if (!data || !fuelSpec?.length) {
    return {
      mainEngine: initStateField<number>(),
      auxEngines: initStateField<number>(),
      boiler: initStateField<number>(),
      igGenerator: initStateField<number>(),
      incinerator: initStateField<number>(),
      dpp: initStateField<number>(),
      otherEngines: initStateField<number>(),
      cargoRelated: initStateField<number>(),
      total: initStateField<number>(),
    };
  }

  return {
    mainEngine: initStateField<number>(data.mainEngine ? data.mainEngine[fuelSpec] : undefined),
    auxEngines: initStateField<number>(data.auxEngine ? data.auxEngine[fuelSpec] : undefined),
    boiler: initStateField<number>(data.auxBoiler ? data.auxBoiler[fuelSpec] : undefined),
    igGenerator: initStateField<number>(data.igGenerator ? data.igGenerator[fuelSpec] : undefined),
    incinerator: initStateField<number>(data.incinerator ? data.incinerator[fuelSpec] : undefined),
    dpp: initStateField<number>(data.dpp ? data.dpp[fuelSpec] : undefined),
    otherEngines: initStateField<number>(data.other ? data.other[fuelSpec] : undefined),
    cargoRelated: initStateField<number>(data.cargoRelated ? data.cargoRelated[fuelSpec] : undefined),
    total: initStateField<number>(data.totals ? data.totals[fuelSpec] : undefined),
  };
};

const generateTotalConsumptionsArray = (data?: ConsumptionsState): TotalConsumptionsFormState[] => {
  const fuelSpecs = data?.fuelSpecs.map((fuelSpec) => fuelSpec.fuelType?.code as string);

  if (!data || !fuelSpecs?.length) {
    return [generateTotalConsumption(), generateTotalConsumption(), generateTotalConsumption()];
  }

  return [
    generateTotalConsumption(data, fuelSpecs[0]),
    generateTotalConsumption(data, fuelSpecs[1]),
    generateTotalConsumption(data, fuelSpecs[2]),
  ];
};

const generateAuxEnginesConsumption = (
  data?: ConsumptionsState,
  fuelSpec?: string | undefined
): BreakdownConsumptionsFormState => {
  if (!data || !fuelSpec?.length) {
    return {
      basicLoad: initStateField<number>(),
      ballastOperations: initStateField<number>(),
      cargoHeating: initStateField<number>(),
      tankCleaning: initStateField<number>(),
      cargoDischarging: initStateField<number>(),
      inertGasSystems: initStateField<number>(),
      bunkerHeating: initStateField<number>(),
    };
  }

  return {
    basicLoad: initStateField<number>(data.aeBasicLoad ? data.aeBasicLoad[fuelSpec] : undefined),
    ballastOperations: initStateField<number>(
      data.aeBallastOperations ? data.aeBallastOperations[fuelSpec] : undefined
    ),
    cargoHeating: initStateField<number>(data.aeCargoHeating ? data.aeCargoHeating[fuelSpec] : undefined),
    tankCleaning: initStateField<number>(data.aeTankCleaning ? data.aeTankCleaning[fuelSpec] : undefined),
    cargoDischarging: initStateField<number>(data.aeCargoShifting ? data.aeCargoShifting[fuelSpec] : undefined),
    inertGasSystems: initStateField<number>(data.aeInertGasSystems ? data.aeInertGasSystems[fuelSpec] : undefined),
    bunkerHeating: initStateField<number>(data.aeBunkerHeating ? data.aeBunkerHeating[fuelSpec] : undefined),
  };
};

const generateAuxEnginesConsumptionsArray = (data?: ConsumptionsState): BreakdownConsumptionsFormState[] => {
  const fuelSpecs = data?.fuelSpecs.map((fuelSpec) => fuelSpec.fuelType?.code as string);

  if (!data || !fuelSpecs?.length) {
    return [generateAuxEnginesConsumption(), generateAuxEnginesConsumption(), generateAuxEnginesConsumption()];
  }

  return [
    generateAuxEnginesConsumption(data, fuelSpecs[0]),
    generateAuxEnginesConsumption(data, fuelSpecs[1]),
    generateAuxEnginesConsumption(data, fuelSpecs[2]),
  ];
};

const generateBoilerConsumption = (
  data?: ConsumptionsState,
  fuelSpec?: string | undefined
): BreakdownConsumptionsFormState => {
  if (!data || !fuelSpec?.length) {
    return {
      basicLoad: initStateField<number>(),
      ballastOperations: initStateField<number>(),
      cargoHeating: initStateField<number>(),
      tankCleaning: initStateField<number>(),
      cargoDischarging: initStateField<number>(),
      inertGasSystems: initStateField<number>(),
      bunkerHeating: initStateField<number>(),
    };
  }

  return {
    basicLoad: initStateField<number>(data.abBasicLoad ? data.abBasicLoad[fuelSpec] : undefined),
    ballastOperations: initStateField<number>(
      data.abBallastOperations ? data.abBallastOperations[fuelSpec] : undefined
    ),
    cargoHeating: initStateField<number>(data.abCargoHeating ? data.abCargoHeating[fuelSpec] : undefined),
    tankCleaning: initStateField<number>(data.abTankCleaning ? data.abTankCleaning[fuelSpec] : undefined),
    cargoDischarging: initStateField<number>(data.abCargoShifting ? data.abCargoShifting[fuelSpec] : undefined),
    inertGasSystems: initStateField<number>(data.abInertGasSystems ? data.abInertGasSystems[fuelSpec] : undefined),
    bunkerHeating: initStateField<number>(data.abBunkerHeating ? data.abBunkerHeating[fuelSpec] : undefined),
  };
};

const generateBoilerConsumptionsArray = (data?: ConsumptionsState): BreakdownConsumptionsFormState[] => {
  const fuelSpecs = data?.fuelSpecs.map((fuelSpec) => fuelSpec.fuelType?.code as string);

  if (!data || !fuelSpecs?.length) {
    return [generateBoilerConsumption(), generateBoilerConsumption(), generateBoilerConsumption()];
  }

  return [
    generateBoilerConsumption(data, fuelSpecs[0]),
    generateBoilerConsumption(data, fuelSpecs[1]),
    generateBoilerConsumption(data, fuelSpecs[2]),
  ];
};

const initWithCarryOver = (data: ConsumptionsState) => {
  return {
    formState: {
      water: {
        fwgNonFunctional: initStateField<boolean>(false),
        freshWaterProduced: initStateField<number>(),
        distilledWaterProduced: initStateField<number>(),
      },
      // V1 carries no run. A run belongs to one report period.
      inertGasSystems: { entries: generateInertGasSystemsArray() },
      cylinderOilConsumption: {
        oilCode: initStateField<SelectOption>(data.cylinderOilConsumption.oilCode),
        oilBn: initStateField<number>(data.cylinderOilConsumption.oilBn),
        oilDensityAt15: initStateField<number>(data.cylinderOilConsumption.oilDensityAt15),
        oilTankTemperature: initStateField<number>(data.cylinderOilConsumption.oilTankTemperature),
        // V1 carries the oil in the tank, not its consumption. The vm
        // calculates the kg consumption and the SLOC from this report.
        oilConsumption: initStateField<number>(),
        oilKgConsumption: initStateField<number>(),
        // oilExpectedKgConsumption: initStateField<number>(
        //   data.cylinderOilConsumption.oilExpectedKgConsumption,
        // ),
        oilSloc: initStateField<number>(),
      },
      // The template carries every fuel column, row by row, as V1 does, so the
      // init builds three fuel columns and fills no value. See the form-rule
      // language doc.
      fuelSpecs: { entries: generateFuelSpecArray() },
      // V1 carries no total. A total counts the fuel of one report period.
      totalConsumptions: { entries: generateTotalConsumptionsArray() },
      // V1 carries no breakdown either. A breakdown splits the total.
      auxEngineConsumptions: { entries: generateAuxEnginesConsumptionsArray() },
      boilerConsumptions: { entries: generateBoilerConsumptionsArray() },
    },
  };
};

const initBlank = () => {
  return {
    formState: {
      water: {
        fwgNonFunctional: initStateField<boolean>(),
        freshWaterProduced: initStateField<number>(),
        distilledWaterProduced: initStateField<number>(),
      },
      inertGasSystems: {
        entries: generateInertGasSystemsArray(),
      },
      cylinderOilConsumption: {
        oilCode: initStateField<SelectOption>(),
        oilBn: initStateField<number>(),
        oilDensityAt15: initStateField<number>(),
        oilTankTemperature: initStateField<number>(),
        oilConsumption: initStateField<number>(),
        oilKgConsumption: initStateField<number>(),
        // oilExpectedKgConsumption: initStateField<number>(),
        oilSloc: initStateField<number>(),
      },
      fuelSpecs: { entries: generateFuelSpecArray() },
      totalConsumptions: { entries: generateTotalConsumptionsArray() },
      auxEngineConsumptions: { entries: generateAuxEnginesConsumptionsArray() },
      boilerConsumptions: { entries: generateBoilerConsumptionsArray() },
    },
  };
};

export const ConsumptionsStore = signalStore(
  { providedIn: 'root' },
  withDevtools('ConsumptionsStore'),
  withState(initBlank()),
  withConsumptionsEffects(),
  withFormStoreReducer(consumptionsFormEvents, initialConsumptionStateReducer, 'consumption'),
  withConsumptionsReducer(),
  withComputed((store) => {
    const vesselSpecStore = inject(VesselSpecStore);
    const powerStore = inject(PowerStore);
    return {
      vm: computed(() => buildConsumptionsVm(store.formState(), powerStore.formState(), vesselSpecStore.vesselSpec())),
    };
  }),
  withMethods((store) => ({
    // Every table of the tab locks a column that holds no fuel type. A cleared
    // select can hold an option with no code, so the test reads the code.
    hasFuelType: (column: number): boolean => !!store.formState().fuelSpecs.entries[column]?.fuelType.value?.code,
  })),
  withFeature((store) =>
    withCalculatedValues(consumptionsFormEvents, () => collectConsumptionsCalculatedValues(store.vm()))
  )
);
